import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { SEOHead } from '../../components/seo/SEOHead';
import { LandingNav } from './LandingNav';
import { LandingFooter } from './LandingFooter';
import { useLandingTheme } from './useLandingTheme';
import { useLandingMotion } from './useLandingMotion';
import { FONT_MONO, FONT_SANS, FONT_SERIF, SUPPORT_EMAIL } from './landingData';
import { DATA_DELETION, LEGAL_ENTITY, LEGAL_UPDATED, PRIVACY, TERMS, type LegalBlock } from './legalContent';
import './landing.css';

const tw = (v: string) => ({ textWrap: v } as React.CSSProperties);
const eyebrow: React.CSSProperties = { fontFamily: FONT_MONO, fontSize: 10, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--hv-leaf)' };

const DOCS = {
    terms: { doc: TERMS, path: '/terms', label: 'Terms of Service' },
    privacy: { doc: PRIVACY, path: '/privacy', label: 'Privacy Policy' },
    deletion: { doc: DATA_DELETION, path: '/data-deletion', label: 'Data Deletion' },
} as const;
type DocKey = keyof typeof DOCS;

// Turn the support address inside copy into a mailto link.
const withEmail = (text: string): React.ReactNode => {
    const parts = text.split(SUPPORT_EMAIL);
    if (parts.length === 1) return text;
    return parts.flatMap((p, i) => (i === 0 ? [p] : [<a key={i} href={`mailto:${SUPPORT_EMAIL}`} style={{ fontWeight: 700 }}>{SUPPORT_EMAIL}</a>, p]));
};

const Block: React.FC<{ block: LegalBlock }> = ({ block }) => {
    if (typeof block === 'string') {
        return <p style={{ margin: '14px 0 0', fontSize: 15, lineHeight: 1.75, color: 'var(--hv-body)', ...tw('pretty') }}>{withEmail(block)}</p>;
    }
    return (
        <ul style={{ listStyle: 'none', margin: '14px 0 0', padding: 0, display: 'grid', gap: 10 }}>
            {block.map((it, i) => (
                <li key={i} style={{ display: 'flex', gap: 12, alignItems: 'flex-start', fontSize: 15, lineHeight: 1.65, color: 'var(--hv-body)' }}>
                    <span aria-hidden="true" style={{ width: 6, height: 6, borderRadius: 999, flex: 'none', marginTop: 10, background: 'var(--hv-leaf)' }} />
                    <span style={tw('pretty')}>{withEmail(it)}</span>
                </li>
            ))}
        </ul>
    );
};

/** `notice`: shown above the document body (e.g. a data-deletion status). */
export const LegalPage: React.FC<{ kind: DocKey; notice?: React.ReactNode }> = ({ kind, notice }) => {
    const { doc, path, label } = DOCS[kind];
    const location = useLocation();
    const { user, isAuthenticated, logout } = useAuth();
    const [theme, toggleTheme] = useLandingTheme();
    const rootRef = useRef<HTMLDivElement>(null);
    const [active, setActive] = useState(doc.sections[0].id);
    useLandingMotion(rootRef);

    // /terms#credits lands on that section; otherwise start at the top.
    useEffect(() => {
        const el = location.hash ? document.getElementById(location.hash.slice(1)) : null;
        if (el) setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 60);
        else window.scrollTo(0, 0);
    }, [location.hash, kind]);

    // Highlight the section being read in the contents rail.
    useEffect(() => {
        setActive(doc.sections[0].id);
        const els = doc.sections.map((s) => document.getElementById(s.id)).filter(Boolean) as HTMLElement[];
        const io = new IntersectionObserver(
            (entries) => {
                const hit = entries.filter((e) => e.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
                if (hit) setActive(hit.target.id);
            },
            { rootMargin: '-15% 0px -70% 0px' },
        );
        els.forEach((el) => io.observe(el));
        return () => io.disconnect();
    }, [doc]);

    return (
        <div ref={rootRef} className="hv-root" data-hv-theme={theme} style={{ position: 'static', fontFamily: FONT_SANS, color: 'var(--hv-ink)', background: 'var(--hv-desk)', overflowX: 'clip', minHeight: '100vh', WebkitFontSmoothing: 'antialiased' }}>
            <SEOHead
                title={label}
                description={doc.seoDescription}
                url={`https://hyvay.com${path}`}
                canonicalUrl={`https://hyvay.com${path}`}
                keywords={['hyvay', label.toLowerCase(), 'legal']}
            />

            <section data-screen-label={`${label} hero`} style={{ position: 'relative', overflow: 'hidden', background: 'var(--hv-page)' }}>
                <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', opacity: 0.7, backgroundImage: 'radial-gradient(circle,var(--hv-line-2) 1.3px,transparent 1.9px)', backgroundSize: '24px 24px', maskImage: 'radial-gradient(ellipse 70% 50% at 50% 0%,black,transparent)', WebkitMaskImage: 'radial-gradient(ellipse 70% 50% at 50% 0%,black,transparent)' }} />
                <LandingNav theme={theme} onToggleTheme={toggleTheme} isAuthenticated={isAuthenticated} avatarUrl={user?.avatar} userName={user?.fullName} onLogout={logout} />

                <div className="hv-shell hv-hero-pad" style={{ position: 'relative', zIndex: 1, maxWidth: 1280, margin: '0 auto', padding: '64px 28px 72px' }}>
                    <div style={{ maxWidth: 720, margin: '0 auto', textAlign: 'center' }}>
                        <div style={eyebrow}>{doc.eyebrow}</div>
                        <h1 className="hv-h1" style={{ margin: '16px 0 0', fontFamily: FONT_SERIF, fontWeight: 400, fontSize: 72, lineHeight: 1.0, letterSpacing: '-0.025em', color: 'var(--hv-ink)', ...tw('balance') }}>
                            {doc.title} <i>{doc.titleEm}</i>
                        </h1>
                        <p style={{ margin: '22px auto 0', maxWidth: 580, fontSize: 16.5, lineHeight: 1.6, color: 'var(--hv-body)', ...tw('pretty') }}>{doc.intro}</p>

                        <div role="tablist" aria-label="Legal documents" style={{ display: 'inline-flex', gap: 4, marginTop: 30, padding: 4, borderRadius: 999, background: 'var(--hv-card)', border: '1px solid var(--hv-line)' }}>
                            {(Object.keys(DOCS) as DocKey[]).map((k) => {
                                const on = k === kind;
                                return (
                                    <Link key={k} to={DOCS[k].path} role="tab" aria-selected={on} className={on ? undefined : 'hv-h-page2'} style={{ display: 'inline-flex', alignItems: 'center', height: 36, padding: '0 16px', borderRadius: 999, fontSize: 13, fontWeight: 700, background: on ? 'var(--brand,#13422C)' : 'transparent', color: on ? '#FFFDF7' : 'var(--hv-ink)' }}>
                                        {DOCS[k].label}
                                    </Link>
                                );
                            })}
                        </div>
                        <div style={{ marginTop: 16, fontFamily: FONT_MONO, fontSize: 10.5, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--hv-muted)' }}>Last updated {LEGAL_UPDATED}</div>
                    </div>
                </div>
            </section>

            <section data-screen-label={`${label} body`} className="hv-band" style={{ padding: '64px 0 96px', background: 'var(--hv-page-2)', borderTop: '1px solid var(--hv-line)' }}>
                <div className="hv-shell hv-legal-grid" style={{ maxWidth: 1180, margin: '0 auto', padding: '0 28px', display: 'grid', gridTemplateColumns: '240px minmax(0,1fr)', gap: 40, alignItems: 'start' }}>
                    <nav aria-label="Contents" className="hv-legal-toc" style={{ position: 'sticky', top: 24 }}>
                        <div style={{ ...eyebrow, color: 'var(--hv-muted)' }}>Contents</div>
                        <ol style={{ listStyle: 'none', margin: '14px 0 0', padding: 0, display: 'grid', gap: 2 }}>
                            {doc.sections.map((s, i) => {
                                const on = s.id === active;
                                return (
                                    <li key={s.id}>
                                        <a href={`#${s.id}`} className={on ? undefined : 'hv-h-card2-ink'} style={{ display: 'flex', gap: 10, padding: '7px 10px', borderRadius: 10, fontSize: 13, lineHeight: 1.4, fontWeight: on ? 700 : 500, background: on ? 'var(--hv-card)' : 'transparent', color: on ? 'var(--hv-ink)' : 'var(--hv-body)', boxShadow: on ? 'inset 2px 0 0 var(--hv-leaf)' : 'none' }}>
                                            <span style={{ fontFamily: FONT_MONO, fontSize: 10.5, paddingTop: 2, color: 'var(--hv-muted)' }}>{String(i + 1).padStart(2, '0')}</span>
                                            <span>{s.title}</span>
                                        </a>
                                    </li>
                                );
                            })}
                        </ol>
                    </nav>

                    <article className="hv-panel" style={{ padding: '40px 44px', borderRadius: 26, background: 'var(--hv-card)', border: '1px solid var(--hv-line)', boxShadow: '0 30px 50px -42px var(--hv-shadow)' }}>
                        {notice && <div style={{ marginBottom: 32, padding: 20, borderRadius: 18, background: 'var(--hv-mint)', color: 'var(--hv-mint-ink)', fontSize: 14, lineHeight: 1.65 }}>{notice}</div>}
                        {doc.sections.map((s, i) => (
                            <section key={s.id} id={s.id} style={{ scrollMarginTop: 24, paddingTop: i === 0 ? 0 : 32, marginTop: i === 0 ? 0 : 32, borderTop: i === 0 ? 0 : '1px solid var(--hv-line)' }}>
                                <div style={{ fontFamily: FONT_MONO, fontSize: 10.5, letterSpacing: '0.06em', color: 'var(--hv-leaf)' }}>{String(i + 1).padStart(2, '0')}</div>
                                <h2 style={{ margin: '8px 0 0', fontFamily: FONT_SERIF, fontWeight: 400, fontSize: 32, lineHeight: 1.1, letterSpacing: '-0.015em', color: 'var(--hv-ink)', ...tw('balance') }}>{s.title}</h2>
                                {s.blocks.map((b, j) => <Block key={j} block={b} />)}
                            </section>
                        ))}

                        <div style={{ marginTop: 40, padding: 22, borderRadius: 20, background: 'var(--hv-mint)' }}>
                            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--hv-mint-ink)' }}>Questions about this page?</h3>
                            <p style={{ margin: '8px 0 0', fontSize: 13.5, lineHeight: 1.65, color: 'var(--hv-mint-ink)', ...tw('pretty') }}>
                                HyVay is operated by {LEGAL_ENTITY}. Write to <a href={`mailto:${SUPPORT_EMAIL}`} style={{ fontWeight: 700, color: 'var(--hv-mint-ink)' }}>{SUPPORT_EMAIL}</a> and we will get back to you.
                            </p>
                        </div>
                    </article>
                </div>
            </section>

            <LandingFooter />
        </div>
    );
};

export default LegalPage;
